import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import {
  FORMULA_FUNCTION_ARITY,
  FORMULA_FUNCTION_NAMES,
  FormulaFunctionName,
  SignalMetadata,
} from "../../../../types/recommendationEngine";
import {
  astToFormulaText,
  formulaTextToSegments,
  parseFormulaText,
  sanitizeEditableText,
} from "../../../../utils/recommendationEngine/formulaText";
import { FUNCTION_DESCRIPTIONS, SIGNAL_GROUP_DESCRIPTIONS } from "../copy";
import { engineTokens } from "../theme";

type Props = {
  formulaText: string;
  onFormulaTextChange: (text: string) => void;
  parseError: string | null;
  signals: SignalMetadata[];
  clamp01: boolean;
};

const QUICK_INSERTS = [" + ", " − ", " × ", " ÷ ", "(", ")"] as const;
const BRAND = engineTokens.brand;
const BRAND_SOFT = engineTokens.brandSoft;
const BRAND_TEXT = "#7C3A02";
const BRAND_REMOVE = "#B45309";

/** Build a non-editable signal pill DOM node. */
const buildChip = (signalId: string): HTMLSpanElement => {
  const chip = document.createElement("span");
  chip.setAttribute("data-gf-chip", "true");
  chip.setAttribute("data-signal-id", signalId);
  chip.setAttribute("contenteditable", "false");
  Object.assign(chip.style, {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    margin: "0 2px",
    padding: "1px 3px 1px 8px",
    background: BRAND_SOFT,
    border: `1px solid ${BRAND}`,
    borderRadius: "6px",
    fontFamily: "inherit",
    fontSize: "0.9em",
    lineHeight: "1.5",
    color: BRAND_TEXT,
    userSelect: "none",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  } as Partial<CSSStyleDeclaration>);

  const label = document.createElement("span");
  label.textContent = signalId;
  chip.appendChild(label);

  const remove = document.createElement("span");
  remove.setAttribute("data-gf-chip-remove", "true");
  remove.textContent = "×";
  remove.setAttribute("role", "button");
  remove.setAttribute("aria-label", `Remove ${signalId}`);
  Object.assign(remove.style, {
    cursor: "pointer",
    fontWeight: "700",
    lineHeight: "1",
    padding: "0 3px",
    borderRadius: "4px",
    color: BRAND_REMOVE,
  } as Partial<CSSStyleDeclaration>);
  chip.appendChild(remove);

  return chip;
};

/** Concatenate the editor's DOM back into formula text (chip → signal id). */
const serializeNode = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? "";
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }
  const el = node as HTMLElement;
  if (el.dataset.gfChip) {
    return el.dataset.signalId ?? "";
  }
  if (el.tagName === "BR") {
    return "";
  }
  let out = "";
  el.childNodes.forEach((child) => {
    out += serializeNode(child);
  });
  return out;
};

const serializeEditor = (editor: HTMLElement): string => {
  let out = "";
  editor.childNodes.forEach((child) => {
    out += serializeNode(child);
  });
  return out;
};

/** Keep both edges as text nodes so the caret can sit before/after a pill. */
const normalizeEdges = (editor: HTMLElement) => {
  const first = editor.firstChild;
  if (!first || first.nodeType !== Node.TEXT_NODE) {
    editor.insertBefore(document.createTextNode(""), first);
  }
  const last = editor.lastChild;
  if (!last || last.nodeType !== Node.TEXT_NODE) {
    editor.appendChild(document.createTextNode(""));
  }
};

/** Replace the whole editor content from a formula string. */
const rebuildEditor = (
  editor: HTMLElement,
  text: string,
  signalIds: string[],
) => {
  while (editor.firstChild) {
    editor.removeChild(editor.firstChild);
  }
  formulaTextToSegments(text, signalIds).forEach((seg) => {
    editor.appendChild(
      seg.type === "text"
        ? document.createTextNode(seg.value)
        : buildChip(seg.signalId),
    );
  });
  normalizeEdges(editor);
};

/** A caret range: the last known in-editor selection, else the editor end. */
const resolveCaretRange = (editor: HTMLElement, saved: Range | null): Range => {
  if (saved) {
    const c = saved.commonAncestorContainer;
    if (c === editor || editor.contains(c)) {
      return saved.cloneRange();
    }
  }
  const sel = window.getSelection();
  if (sel && sel.rangeCount) {
    const r = sel.getRangeAt(0);
    const c = r.commonAncestorContainer;
    if (c === editor || editor.contains(c)) {
      return r.cloneRange();
    }
  }
  const end = document.createRange();
  end.selectNodeContents(editor);
  end.collapse(false);
  return end;
};

/** Insert nodes at the caret range and drop the caret right after them. */
const insertAtRange = (editor: HTMLElement, range: Range, nodes: Node[]) => {
  const fragment = document.createDocumentFragment();
  nodes.forEach((n) => fragment.appendChild(n));
  const lastNode = nodes[nodes.length - 1];
  range.deleteContents();
  range.insertNode(fragment);
  normalizeEdges(editor);

  const caret = document.createRange();
  caret.setStartAfter(lastNode);
  caret.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(caret);
};

const formatRange = (vr: SignalMetadata["valueRange"]): string =>
  `${vr.min} – ${vr.max === null ? "∞" : vr.max}`;

/**
 * Plain-language range label matching SignalDetailMenuItem so the Text
 * Editor's signal picker reads identically to the other two tabs.
 */
const formatRangeLabel = (vr: SignalMetadata["valueRange"]): string => {
  if (vr.max === null) {
    return `${vr.min}+ unbounded`;
  }
  if (vr.min === 0 && vr.max === 1) {
    return "0–1 normalized";
  }
  return `${vr.min}–${vr.max}`;
};

const FormulaTextEditorTab = ({
  formulaText,
  onFormulaTextChange,
  parseError,
  signals,
  clamp01,
}: Props) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const lastSerializedRef = useRef<string | null>(null);
  const lastSignalKeyRef = useRef<string>("");
  const onChangeRef = useRef(onFormulaTextChange);
  onChangeRef.current = onFormulaTextChange;

  const [signalMenuOpen, setSignalMenuOpen] = useState(false);
  const [signalSearch, setSignalSearch] = useState("");
  const functionAnchorRef = useRef<HTMLButtonElement>(null);
  const [functionMenuOpen, setFunctionMenuOpen] = useState(false);

  const signalIds = useMemo(() => signals.map((s) => s.id), [signals]);
  const signalKey = signalIds.join("|");
  const signalById = useMemo(() => {
    const map: Record<string, SignalMetadata> = {};
    signals.forEach((s) => {
      map[s.id] = s;
    });
    return map;
  }, [signals]);

  /* --- write formula text back to the parent ---------------------- */
  const publish = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    const text = serializeEditor(editor);
    lastSerializedRef.current = text;
    onChangeRef.current(text);
  }, []);

  const insertNodes = useCallback(
    (nodes: Node[]) => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }
      editor.focus();
      const range = resolveCaretRange(editor, savedRangeRef.current);
      insertAtRange(editor, range, nodes);
      publish();
    },
    [publish],
  );

  const insertSignal = useCallback(
    (signalId: string) => insertNodes([buildChip(signalId)]),
    [insertNodes],
  );

  const insertText = useCallback(
    (raw: string) => {
      const clean = sanitizeEditableText(raw);
      if (clean) {
        insertNodes([document.createTextNode(clean)]);
      }
    },
    [insertNodes],
  );

  /**
   * Insert raw text (including letters) without going through
   * sanitizeEditableText. Reserved for explicit template insertion
   * such as math-function calls; typed input still goes through the
   * sanitiser so users can't manually fake signal ids.
   */
  const insertRawText = useCallback(
    (raw: string) => {
      if (!raw) {
        return;
      }
      insertNodes([document.createTextNode(raw)]);
    },
    [insertNodes],
  );

  const insertFunctionCall = useCallback(
    (name: FormulaFunctionName) => {
      const arity = FORMULA_FUNCTION_ARITY[name];
      // For multi-arg functions, seed commas so the user sees the right shape;
      // for single-arg functions, leave the parens empty.
      const placeholders =
        arity.min > 1
          ? Array.from({ length: arity.min }, () => " ").join(",")
          : "";
      insertRawText(`${name}(${placeholders})`);
    },
    [insertRawText],
  );

  const clearFormula = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    while (editor.firstChild) {
      editor.removeChild(editor.firstChild);
    }
    normalizeEdges(editor);
    editor.focus();
    publish();
  }, [publish]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) {
      return undefined;
    }

    const handleInput = () => publish();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        return;
      }
      // Signals are insert-only — block typed identifier characters.
      if (e.key.length === 1 && /[a-zA-Z_]/.test(e.key)) {
        e.preventDefault();
      }
    };

    const handleBeforeInput = (e: Event) => {
      const data = (e as InputEvent).data;
      if (data && /[a-zA-Z_]/.test(data)) {
        e.preventDefault();
        const clean = sanitizeEditableText(data);
        if (clean) {
          insertText(clean);
        }
      }
    };

    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData?.getData("text/plain") ?? "";
      insertText(pasted.replace(/\s+/g, " "));
    };

    const handleDrop = (e: DragEvent) => e.preventDefault();

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.dataset.gfChipRemove) {
        const chip = target.closest("[data-gf-chip]");
        if (chip) {
          chip.remove();
          normalizeEdges(editor);
          editor.focus();
          publish();
        }
      }
    };

    const handleSelectionChange = () => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount) {
        const range = sel.getRangeAt(0);
        const c = range.commonAncestorContainer;
        if (c === editor || editor.contains(c)) {
          savedRangeRef.current = range.cloneRange();
        }
      }
    };

    editor.addEventListener("input", handleInput);
    editor.addEventListener("keydown", handleKeyDown);
    editor.addEventListener("beforeinput", handleBeforeInput);
    editor.addEventListener("paste", handlePaste);
    editor.addEventListener("drop", handleDrop);
    editor.addEventListener("click", handleClick);
    document.addEventListener("selectionchange", handleSelectionChange);

    return () => {
      editor.removeEventListener("input", handleInput);
      editor.removeEventListener("keydown", handleKeyDown);
      editor.removeEventListener("beforeinput", handleBeforeInput);
      editor.removeEventListener("paste", handlePaste);
      editor.removeEventListener("drop", handleDrop);
      editor.removeEventListener("click", handleClick);
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [publish, insertText]);

  /* --- rebuild the editor when text/signals change externally ----- */
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    const signalsChanged = lastSignalKeyRef.current !== signalKey;
    if (formulaText === lastSerializedRef.current && !signalsChanged) {
      return;
    }
    lastSerializedRef.current = formulaText;
    lastSignalKeyRef.current = signalKey;
    rebuildEditor(editor, formulaText, signalIds);
  }, [formulaText, signalKey, signalIds]);

  /* --- derived: live preview + signals currently in use ----------- */
  const preview = useMemo(() => {
    if (!formulaText.trim()) {
      return null;
    }
    const { node, error } = parseFormulaText(formulaText, signalIds);
    return node && !error ? astToFormulaText(node) : null;
  }, [formulaText, signalIds]);

  const usedSignals = useMemo(() => {
    const seen: string[] = [];
    formulaTextToSegments(formulaText, signalIds).forEach((seg) => {
      if (seg.type === "signal" && !seen.includes(seg.signalId)) {
        seen.push(seg.signalId);
      }
    });
    return seen
      .map((id) => signalById[id])
      .filter((s): s is SignalMetadata => Boolean(s));
  }, [formulaText, signalIds, signalById]);

  const filteredSignals = useMemo(() => {
    const q = signalSearch.trim().toLowerCase();
    if (!q) {
      return signals;
    }
    return signals.filter(
      (s) =>
        s.id.toLowerCase().includes(q) ||
        s.label.toLowerCase().includes(q) ||
        s.group.toLowerCase().includes(q),
    );
  }, [signals, signalSearch]);

  const groupedFiltered = useMemo(() => {
    const groups: Record<string, SignalMetadata[]> = {};
    filteredSignals.forEach((s) => {
      if (!groups[s.group]) {
        groups[s.group] = [];
      }
      groups[s.group].push(s);
    });
    return groups;
  }, [filteredSignals]);

  const isEmpty = !formulaText.trim();

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Type numbers, operators and parentheses directly. Use{" "}
        <strong>Add signal</strong> to drop in a locked signal token — signals
        cannot be typed; remove one with its × or with Backspace beside it. To
        add math functions (sqrt, min, max, etc.), switch to the{" "}
        <strong>Expression tree</strong> tab.
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Tooltip
          title="Pick a signal from the catalog and drop it into the formula as a locked token."
          placement="top"
          arrow
        >
          <Button
            ref={anchorRef}
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setSignalMenuOpen((o) => !o)}
          >
            Add signal
          </Button>
        </Tooltip>
        <Tooltip
          title="Insert a math function call (sqrt, log, min, etc.) at the cursor."
          placement="top"
          arrow
        >
          <Button
            ref={functionAnchorRef}
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setFunctionMenuOpen((o) => !o)}
          >
            Add function
          </Button>
        </Tooltip>
        {QUICK_INSERTS.map((snippet) => (
          <Tooltip
            key={snippet}
            title={`Insert "${snippet.trim() || snippet}" at the cursor.`}
            placement="top"
            arrow
          >
            <Button
              size="small"
              variant="text"
              onClick={() => insertText(snippet)}
              sx={{ minWidth: 36, fontFamily: "monospace" }}
            >
              {snippet.trim() || snippet}
            </Button>
          </Tooltip>
        ))}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="text"
          color="inherit"
          startIcon={<ClearIcon />}
          onClick={clearFormula}
          disabled={isEmpty}
          sx={{ color: "text.secondary" }}
        >
          Clear
        </Button>
      </Box>

      <Box sx={{ position: "relative" }}>
        <Box
          component="div"
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          spellCheck={false}
          role="textbox"
          aria-multiline="false"
          aria-label="Formula"
          sx={{
            border: `1px solid ${engineTokens.borderStrong}`,
            borderRadius: 2,
            p: 1.5,
            minHeight: 110,
            bgcolor: engineTokens.bgPaper,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: "14px",
            lineHeight: 2,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            outline: "none",
            cursor: "text",
            "&:focus": {
              borderColor: BRAND,
              boxShadow: `0 0 0 1px ${BRAND}`,
            },
          }}
        />
        {isEmpty && (
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              color: "text.disabled",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: "14px",
              pointerEvents: "none",
            }}
          >
            e.g. 0.5 × signal + 0.3 × (signal ÷ 2)
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          mt: 1,
          display: "flex",
          gap: 1,
          alignItems: "baseline",
          minHeight: 22,
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Preview:
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: "monospace",
            color: preview ? "text.primary" : "text.disabled",
          }}
        >
          {preview ?? (isEmpty ? "—" : "incomplete")}
        </Typography>
      </Box>

      <Box
        sx={{
          mt: 1,
          border: `1px solid ${engineTokens.border}`,
          borderRadius: 2,
          p: 1.25,
          bgcolor: engineTokens.bgMuted,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "text.secondary" }}
        >
          Signals in use ({usedSignals.length})
        </Typography>
        {usedSignals.length === 0 ? (
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 0.5, color: "text.disabled" }}
          >
            No signals added yet.
          </Typography>
        ) : (
          <Box
            sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            {usedSignals.map((s) => (
              <Box
                key={s.id}
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "baseline",
                  flexWrap: "wrap",
                }}
              >
                <Tooltip title={s.description || ""} placement="top-start">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {s.label}
                  </Typography>
                </Tooltip>
                <Typography
                  variant="caption"
                  sx={{ fontFamily: "monospace", color: "text.secondary" }}
                >
                  {s.id}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  range {formatRange(s.valueRange)}
                </Typography>
                {s.isRaw && (
                  <Box
                    component="span"
                    sx={{
                      px: 0.75,
                      py: 0.1,
                      borderRadius: 1,
                      fontSize: "10px",
                      fontWeight: 700,
                      bgcolor: clamp01
                        ? "rgba(245, 158, 11, 0.2)"
                        : engineTokens.slate200,
                      color: clamp01 ? "#92400E" : engineTokens.slate600,
                    }}
                  >
                    RAW
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Popper
        open={signalMenuOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={() => setSignalMenuOpen(false)}>
          <Paper
            elevation={8}
            sx={{
              width: 440,
              maxHeight: 480,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              mt: 0.5,
            }}
          >
            <Box
              sx={{ p: 1, borderBottom: `1px solid ${engineTokens.border}` }}
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Search signals…"
                value={signalSearch}
                onChange={(e) => setSignalSearch(e.target.value)}
                autoFocus
              />
            </Box>
            <List dense sx={{ overflow: "auto", flex: 1, py: 0 }}>
              {Object.entries(groupedFiltered).map(([group, items]) => (
                <React.Fragment key={group}>
                  <Box
                    sx={{
                      px: 2,
                      pt: 1,
                      pb: 0.5,
                      borderBottom: `1px solid ${engineTokens.border}`,
                      backgroundColor: engineTokens.bgMuted,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, letterSpacing: "0.04em" }}
                    >
                      {group.toUpperCase()}
                    </Typography>
                    {SIGNAL_GROUP_DESCRIPTIONS[group] && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{ whiteSpace: "normal", lineHeight: 1.3 }}
                      >
                        {SIGNAL_GROUP_DESCRIPTIONS[group]}
                      </Typography>
                    )}
                  </Box>
                  {items.map((s) => (
                    <ListItemButton
                      key={s.id}
                      onClick={() => {
                        insertSignal(s.id);
                        setSignalMenuOpen(false);
                        setSignalSearch("");
                      }}
                      sx={{ alignItems: "flex-start", py: 1 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.25,
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {s.label}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              s.isRaw
                                ? "Raw count"
                                : formatRangeLabel(s.valueRange)
                            }
                            color={s.isRaw && clamp01 ? "warning" : "default"}
                            variant="outlined"
                            sx={{ height: 18, fontSize: 11 }}
                          />
                          <Chip
                            size="small"
                            label={s.group}
                            variant="outlined"
                            sx={{ height: 18, fontSize: 11 }}
                          />
                          {s.formulaHint && (
                            <Tooltip
                              title={s.formulaHint}
                              placement="top"
                              arrow
                            >
                              <Chip
                                size="small"
                                label="Formula tip"
                                color="primary"
                                variant="outlined"
                                sx={{
                                  height: 18,
                                  fontSize: 11,
                                  cursor: "help",
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        {(s.summary || s.description) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: "normal", lineHeight: 1.3 }}
                          >
                            {s.summary || s.description}
                          </Typography>
                        )}
                        {(s.relatedCandidateSources?.length ?? 0) > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.25,
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              From sources:
                            </Typography>
                            {(s.relatedCandidateSources ?? []).map((src) => (
                              <Chip
                                key={src}
                                size="small"
                                label={src}
                                variant="outlined"
                                sx={{ height: 16, fontSize: 10 }}
                              />
                            ))}
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            color: "text.secondary",
                          }}
                        >
                          {s.id}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  ))}
                </React.Fragment>
              ))}
              {filteredSignals.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{ p: 2, color: "text.secondary" }}
                >
                  No signals match your search.
                </Typography>
              )}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>

      <Popper
        open={functionMenuOpen}
        anchorEl={functionAnchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={() => setFunctionMenuOpen(false)}>
          <Paper
            elevation={8}
            sx={{
              width: 320,
              maxHeight: 360,
              overflow: "auto",
              mt: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                px: 2,
                pt: 1,
                display: "block",
                color: "text.secondary",
                fontWeight: 600,
              }}
            >
              MATH FUNCTIONS
            </Typography>
            <List dense sx={{ py: 0 }}>
              {FORMULA_FUNCTION_NAMES.map((fn) => {
                const arity = FORMULA_FUNCTION_ARITY[fn];
                const arityLabel =
                  arity.min === arity.max
                    ? `${arity.min} arg${arity.min === 1 ? "" : "s"}`
                    : `${arity.min}+ args`;
                return (
                  <ListItemButton
                    key={fn}
                    onClick={() => {
                      insertFunctionCall(fn);
                      setFunctionMenuOpen(false);
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          component="span"
                          sx={{ display: "inline-flex", gap: 1 }}
                        >
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontWeight: 600 }}
                          >
                            {fn}(…)
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            {arityLabel}
                          </Typography>
                        </Box>
                      }
                      secondary={FUNCTION_DESCRIPTIONS[fn]}
                      secondaryTypographyProps={{
                        variant: "caption",
                        sx: { whiteSpace: "normal" },
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {parseError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {parseError}
        </Alert>
      )}
    </Box>
  );
};

export default FormulaTextEditorTab;
